import { computed, ref, watch, Ref } from '@vue/composition-api';
import { debounce } from 'lodash-es';
import {
  InterestBehaviorScene,
  InterestBehaviorTab,
  InterestBehaviorData,
  AdgroupFormData,
  CampaignFormData,
} from '@tt4b/ttam-enums';
import { fuzzySearch } from '@tt4b/ttam-hooks';
import { generateId } from './../../../../utils/index';
import { useInterestBehaviorStore, formatHashtagLabelName } from './use-interest-behavior-store';
import { useTargetTypePropsVisible } from '../../interests/hooks/target-type';
import { useInterestBehaviorTracer } from './use-interest-behavior-tracer';

// 搜索
export function useFuzzySearch(props: { adgroupData: Ref<AdgroupFormData>; campaignData: Ref<CampaignFormData> }) {
  const { campaignData, adgroupData } = props;
  const isSearching = ref(false);
  const searchKeyword = ref('');
  const searchList = ref<InterestBehaviorData[]>([]);
  const { visible: isInMarketTargetAvailable } = useTargetTypePropsVisible({
    campaignData,
    adgroupData,
  });
  const { interestBehaviorListMap } = useInterestBehaviorStore();

  const scenesSet = new Set([
    InterestBehaviorScene.INTEREST,
    InterestBehaviorScene.VIDEO,
    InterestBehaviorScene.CREATOR,
    InterestBehaviorScene.HASHTAG,
    InterestBehaviorScene.KEYWORD,
    InterestBehaviorScene.INMARKET,
  ]);
  // 不支持定向则不需要搜索INMARKET
  const scenes = computed(() => {
    if (isInMarketTargetAvailable.value) {
      scenesSet.add(InterestBehaviorScene.INMARKET);
    } else {
      scenesSet.delete(InterestBehaviorScene.INMARKET);
    }

    return Array.from(scenesSet);
  });
  // 改变搜索
  const onSearchKeywordChange = val => {
    searchKeyword.value = val;
  };

  /**
   * 给搜索结果中的tag添加父级，思路是：
   *  interest，video，creator存在父子关系，根据tag_id获取map中的item obj，替换搜索结果中的tag obj即可
   *  兴趣关键词和hashtag不存在父子关系
   * @param tag 搜索结果中的tag obj
   * @returns
   */
  const handleSearchResultFatherSonRelationship = tag => {
    const { scene } = tag;
    if (scene === InterestBehaviorScene.KEYWORD || scene === InterestBehaviorScene.HASHTAG) {
      return tag;
    }
    const currentSceneListMap = interestBehaviorListMap.value[scene] || {};
    return currentSceneListMap[tag.tag_id] || tag;
  };

  const search = debounce(
    async (keyword: string) => {
      if (keyword) {
        isSearching.value = true;
        const tags = await fuzzySearch({
          keyword,
          scenes: scenes.value,
          industryTypes: campaignData.value.industryTypes,
          inventoryFlow: adgroupData.value.inventoryFlow,
        });
        isSearching.value = false;
        searchList.value = tags.map(tag => ({
          ...handleSearchResultFatherSonRelationship(tag),
          label: tag.tag_name,
          tag_name:
            tag.scene === InterestBehaviorScene.HASHTAG
              ? formatHashtagLabelName(tag.tag_type, tag.tag_name)
              : tag.tag_name,
          type: InterestBehaviorTab()[tag.scene].title,
          id: generateId(tag.scene, tag.tag_id),
        }));
      }
    },
    500,
    { leading: false, trailing: true, maxWait: 500 },
  );

  function subscribeFuzzySearch() {
    watch(searchKeyword, value => {
      search(value);
    });
  }

  const { onHideSearchResultList, onExposeResultListItem, afterClickResultListItem, onSelectTreeInteraction } =
    useInterestBehaviorTracer({
      keyword: searchKeyword,
      searchResult: searchList,
      type: 'five-in-one',
      adgroupData,
    });

  return {
    search,
    isSearching,
    searchKeyword,
    searchList,
    onSearchKeywordChange,
    onHideSearchResultList,
    onExposeResultListItem,
    afterClickResultListItem,
    onSelectTreeInteraction,
    subscribeFuzzySearch,
  };
}
